<script setup lang="ts">
import { computed, ref, useId } from 'vue'
import { NCheckbox } from 'naive-ui'
import type { ProductOwnerBacklogDay } from '../stores/product_owner_backlog'
import {
  CHART_AXIS_LABEL_FILL,
  CHART_CROSSHAIR_STROKE,
  CHART_EMPHASIS_FILL,
  CHART_GRID_STROKE,
  CHART_MUTED_FILL,
  CHART_OVERLAY_DASH,
  CHART_SERIES_COLOR,
  CHART_SERIES_FILL,
} from '../composables/chartTheme'
import {
  areaPath,
  buildBacklogFrame,
  buildBacklogSeries,
  buildXAxisLabels,
  clientXToViewX,
  deltaColorClass,
  emptySpanNote,
  formatDelta,
  formatShortDate,
  humanOverlaySince,
  MAX_MARKED_POINTS,
  nearestPoint,
  peakLabel,
  pointTitle,
  polylinePoints,
  PLOT,
  VIEW_HEIGHT,
  VIEW_WIDTH,
  yFor,
} from '../composables/productOwnerBacklogChart'

// Backlog burndown for the Activity tab's per-repo section: the total open
// issue count per daily snapshot as a filled line, stretched across the whole
// selected window, with the human-filed subset as an optional dashed overlay
// on the same scale.
const props = defineProps<{
  days: ProductOwnerBacklogDay[]
  windowDays: number
}>()

const showHuman = ref(false)
const dataListId = useId()

const frame = computed(() => buildBacklogFrame(props.days, props.windowDays))
const total = computed(() => (frame.value ? buildBacklogSeries(props.days, 'total', frame.value) : null))
const hasHumanCounts = computed(() => props.days.some((d) => d.human_open_issue_count != null))
const human = computed(() =>
  frame.value && hasHumanCounts.value && showHuman.value ? buildBacklogSeries(props.days, 'human', frame.value) : null,
)
const humanSince = computed(() => humanOverlaySince(props.days))
const humanByDate = computed(() => new Map(human.value?.points.map((p) => [p.date, p.value])))

const humanLabel = computed(() =>
  humanSince.value ? `Overlay human-filed (since ${formatShortDate(humanSince.value)})` : 'Human-filed (no data)',
)

const points = computed(() => total.value?.points ?? [])
const latestPoint = computed(() => points.value[points.value.length - 1])
const showMarkers = computed(() => points.value.length <= MAX_MARKED_POINTS)
const since = computed(() => points.value[0]?.date ?? null)
const deltaText = computed(() => formatDelta(total.value?.delta ?? null, since.value))
const peak = computed(() => peakLabel(points.value))
const xLabels = computed(() => (frame.value ? buildXAxisLabels(frame.value) : []))
const emptyNote = computed(() => (frame.value && since.value ? emptySpanNote(frame.value, since.value) : null))

const activeDate = ref<string | null>(null)
const activePoint = computed(() => points.value.find((p) => p.date === activeDate.value) ?? null)
const readout = computed(() => {
  const point = activePoint.value
  if (!point) return null
  const onRight = point.x > (PLOT.left + PLOT.right) / 2
  return {
    x: point.x + (onRight ? -6 : 6),
    anchor: onRight ? 'end' : 'start',
    text: pointTitle(point, humanByDate.value.get(point.date) ?? null),
  }
})

function trackPointer(event: PointerEvent) {
  const svg = (event.currentTarget as Element).closest('svg')
  if (!svg) return
  const viewX = clientXToViewX(event.clientX, svg.getBoundingClientRect())
  if (viewX === null) return
  activeDate.value = nearestPoint(points.value, viewX)?.date ?? null
}

const ariaLabel = computed(() => {
  const first = points.value[0]
  const last = latestPoint.value
  if (!first || !last) return 'Backlog burndown: no open issues recorded in this window'
  return `Backlog burndown, total open issues: ${first.value} on ${first.date}, ${last.value} on ${last.date}, ${deltaText.value}`
})
</script>

<template>
  <div v-if="frame && total" class="flex flex-col gap-2" data-testid="product-owner-activity-backlog-chart">
    <div class="flex flex-wrap items-baseline gap-x-3 gap-y-1">
      <span class="text-2xl font-semibold text-white tabular-nums" data-testid="product-owner-activity-backlog-latest">
        {{ total.latest }}
      </span>
      <span class="text-xs text-white/60">open issues</span>
      <span
        class="text-sm font-medium"
        :class="deltaColorClass(total.delta)"
        data-testid="product-owner-activity-backlog-delta"
      >
        {{ deltaText }}
      </span>
    </div>

    <div class="overflow-x-auto">
      <svg
        :viewBox="`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`"
        role="img"
        :aria-label="ariaLabel"
        :aria-describedby="dataListId"
        class="block w-full min-w-[480px] h-auto"
      >
        <g class="text-[12px]" :fill="CHART_AXIS_LABEL_FILL">
          <template v-for="tick in frame.yTicks" :key="`tick-${tick}`">
            <line
              :x1="PLOT.left"
              :x2="PLOT.right"
              :y1="yFor(frame, tick)"
              :y2="yFor(frame, tick)"
              :stroke="CHART_GRID_STROKE"
            />
            <text :x="PLOT.left - 6" :y="yFor(frame, tick) + 4" text-anchor="end">{{ tick }}</text>
          </template>
          <text
            v-for="label in xLabels"
            :key="`x-${label.date}`"
            :x="label.x"
            :y="VIEW_HEIGHT - 4"
            :text-anchor="label.anchor"
          >
            {{ label.text }}
          </text>
          <text
            v-if="peak"
            :x="peak.x"
            :y="peak.y"
            :text-anchor="peak.anchor"
            :fill="CHART_EMPHASIS_FILL"
            data-testid="product-owner-activity-backlog-peak"
          >
            {{ peak.text }}
          </text>
          <text
            v-if="emptyNote"
            :x="emptyNote.x"
            :y="(PLOT.top + PLOT.bottom) / 2"
            text-anchor="middle"
            :fill="CHART_MUTED_FILL"
            data-testid="product-owner-activity-backlog-empty-span"
          >
            {{ emptyNote.text }}
          </text>
        </g>

        <path
          v-for="(segment, i) in total.segments"
          :key="`area-${i}`"
          :d="areaPath(segment)"
          :fill="CHART_SERIES_FILL"
        />
        <polyline
          v-for="(segment, i) in total.segments"
          :key="`total-${i}`"
          :points="polylinePoints(segment)"
          fill="none"
          :stroke="CHART_SERIES_COLOR"
          stroke-width="2"
          stroke-linejoin="round"
          data-testid="product-owner-activity-backlog-line"
        />
        <template v-if="human">
          <polyline
            v-for="(segment, i) in human.segments"
            :key="`human-${i}`"
            :points="polylinePoints(segment)"
            fill="none"
            :stroke="CHART_EMPHASIS_FILL"
            stroke-width="2"
            :stroke-dasharray="CHART_OVERLAY_DASH"
            data-testid="product-owner-activity-backlog-human-line"
          />
        </template>
        <template v-if="showMarkers">
          <circle
            v-for="point in points"
            :key="`dot-${point.date}`"
            :cx="point.x.toFixed(1)"
            :cy="point.y.toFixed(1)"
            r="2.5"
            :fill="CHART_SERIES_COLOR"
          />
        </template>
        <circle
          v-if="latestPoint"
          :cx="latestPoint.x.toFixed(1)"
          :cy="latestPoint.y.toFixed(1)"
          r="4"
          :fill="CHART_SERIES_COLOR"
          stroke="white"
          stroke-width="1.5"
          data-testid="product-owner-activity-backlog-latest-point"
        />
        <template v-if="activePoint && readout">
          <line
            :x1="activePoint.x"
            :x2="activePoint.x"
            :y1="PLOT.top"
            :y2="PLOT.bottom"
            :stroke="CHART_CROSSHAIR_STROKE"
            stroke-dasharray="3 3"
            data-testid="product-owner-activity-backlog-crosshair"
          />
          <circle
            :cx="activePoint.x"
            :cy="activePoint.y"
            r="4.5"
            :fill="CHART_SERIES_COLOR"
            stroke="white"
            stroke-width="1.5"
          />
          <text
            :x="readout.x"
            :y="PLOT.top + 10"
            :text-anchor="readout.anchor"
            class="text-[12px]"
            fill="white"
            data-testid="product-owner-activity-backlog-readout"
          >
            {{ readout.text }}
          </text>
        </template>
        <rect
          :x="PLOT.left"
          :y="PLOT.top"
          :width="PLOT.right - PLOT.left"
          :height="PLOT.bottom - PLOT.top"
          fill="transparent"
          class="touch-pan-y"
          data-testid="product-owner-activity-backlog-hit-area"
          @pointerdown="trackPointer"
          @pointermove="trackPointer"
          @pointerleave="activeDate = null"
        />
      </svg>
    </div>
    <ul :id="dataListId" class="sr-only">
      <li v-for="point in points" :key="point.date">
        {{ pointTitle(point, humanByDate.get(point.date) ?? null) }}
      </li>
    </ul>

    <div class="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-white/60">
      <span class="flex items-center gap-1.5">
        <svg width="16" height="8" aria-hidden="true">
          <line x1="0" x2="16" y1="4" y2="4" :stroke="CHART_SERIES_COLOR" stroke-width="2" />
        </svg>
        Total
      </span>
      <span class="flex items-center gap-1.5">
        <svg width="16" height="8" aria-hidden="true">
          <line
            x1="0"
            x2="16"
            y1="4"
            y2="4"
            :stroke="CHART_EMPHASIS_FILL"
            stroke-width="2"
            :stroke-dasharray="CHART_OVERLAY_DASH"
          />
        </svg>
        <NCheckbox
          v-model:checked="showHuman"
          size="small"
          :disabled="!hasHumanCounts"
          data-testid="product-owner-activity-backlog-show-human"
        >
          {{ humanLabel }}
        </NCheckbox>
      </span>
    </div>
  </div>
</template>
